/**
 * What narrows the list, and what orders it.
 *
 * Every control here changes the question asked of the server; nothing
 * filters rows already on the page. The options come from the organization's
 * own taxonomy and its own people, not from whatever happens to be listed.
 *
 * With text typed the order is relevance, and the sort control is replaced
 * by a sentence saying so: offering "Title, A to Z" over a search would be
 * offering something the search does not do.
 */
import { useId } from 'react';
import type { TaxonomyItem } from '../../admin/useTaxonomy';
import { hasFilters, isOwnerQueue, sortsFor, withoutFilters, type LibrarySort, type LibraryState } from './libraryQuery';
import { SORT_OPTIONS } from './libraryText';
import type { LibraryWho } from './useLibrary';
import styles from './Library.module.css';

function Pick({ label, value, options, any, onChange }: {
  label: string;
  value: string | null;
  options: { id: string; name: string }[];
  any: string;
  onChange: (id: string | null) => void;
}) {
  const id = useId();
  return (
    <div className={styles.filter}>
      <label className={styles.filterLabel} htmlFor={id}>{label}</label>
      <select
        id={id}
        className={value ? `${styles.filterSelect} ${styles.filterOn}` : styles.filterSelect}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
      >
        <option value="">{any}</option>
        {/* A value in the address that is no longer offered still shows as chosen, so it can be seen and cleared. */}
        {value && !options.some((o) => o.id === value) && <option value={value}>No longer available</option>}
        {options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
      </select>
    </div>
  );
}

export interface FilterOptions {
  departments: TaxonomyItem[];
  categories: TaxonomyItem[];
  documentTypes: TaxonomyItem[];
  owners: { uid: string; name: string }[];
}

export function LibraryFilters({ who, state, options, searching, onChange, hideTaxonomy = false, formerOwners = [], range = null }: {
  who: LibraryWho;
  state: LibraryState;
  options: FilterOptions;
  searching: boolean;
  onChange: (patch: Partial<LibraryState>) => void;
  /** A reader browses department and category from the panel beside the list, so they are not repeated here. */
  hideTaxonomy?: boolean;
  /** People who have left, for narrowing the Owner inactive queue to one of them. */
  formerOwners?: { uid: string; name: string }[];
  /** "1–50 of 4,782 policies", said here as well as under the table, so it is known without scrolling. */
  range?: string | null;
}) {
  const sortId = useId();
  const queue = isOwnerQueue(state.view);
  const active = (items: TaxonomyItem[]) => items.filter((i) => i.isActive).map((i) => ({ id: i.id, name: i.name }));
  const allowed = sortsFor(who);
  const sorts = SORT_OPTIONS.filter((o) => {
    const key = o.value.split(':')[0] as LibrarySort;
    if (queue) return key === 'title' || key === 'number';
    if (state.view === 'reviewOverdue' || state.view === 'reviewDue') return key === 'review';
    return allowed.includes(key);
  });

  return (
    <div className={styles.filters}>
      {!queue && !hideTaxonomy && (
        <>
          <Pick label="Department" value={state.department} options={active(options.departments)} any="Any department" onChange={(department) => onChange({ department })} />
          <Pick label="Category" value={state.category} options={active(options.categories)} any="Any category" onChange={(category) => onChange({ category })} />
        </>
      )}
      {!queue && (
        <Pick label="Document type" value={state.documentType} options={active(options.documentTypes)} any="Any document type" onChange={(documentType) => onChange({ documentType })} />
      )}
      {!queue && who.ownersShown && (
        <>
          {!state.mine && (
            <Pick label="Owner" value={state.owner} options={options.owners.map((o) => ({ id: o.uid, name: o.name }))} any="Any owner" onChange={(owner) => onChange({ owner })} />
          )}
          <label className={state.mine ? `${styles.mine} ${styles.mineOn}` : styles.mine}>
            <input type="checkbox" checked={state.mine} onChange={(e) => onChange({ mine: e.target.checked })} />
            My policies
          </label>
        </>
      )}
      {state.view === 'ownerInactive' && formerOwners.length > 1 && (
        <Pick label="Former owner" value={state.owner} options={formerOwners.map((o) => ({ id: o.uid, name: o.name }))} any="Everybody who has left" onChange={(owner) => onChange({ owner })} />
      )}
      {hasFilters(state) && (
        <button type="button" className={styles.linkBtn} style={{ paddingBottom: 8 }} onClick={() => onChange(withoutFilters(state))}>
          Clear filters
        </button>
      )}
      <span className={styles.spacer} />
      {range && <span className={styles.relevance} aria-hidden="true">{range}</span>}
      {searching ? (
        <span className={styles.relevance}>Sorted by best match</span>
      ) : sorts.length < 2 ? null : (
        <div className={styles.filter}>
          <label className={styles.filterLabel} htmlFor={sortId}>Sort</label>
          <select
            id={sortId}
            className={styles.filterSelect}
            value={`${state.sort}:${state.dir}`}
            onChange={(e) => {
              const [sort, dir] = e.target.value.split(':') as [LibrarySort, 'asc' | 'desc'];
              onChange({ sort, dir });
            }}
          >
            {sorts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      )}
    </div>
  );
}
